<template>
  <div class="section overflow-hidden position-relative min-height" id="wrapper">

    <!-- Page Content Wrapper Start -->
    <div class="page-content-wrapper padding-left-0">
      <div class="container-fluid custom-container">

        <!-- Message Start -->
        <div class="" >
          <div class="message-content">
            <h3>Team Members:</h3>
          </div>
        </div>
        <!-- Message End -->

        <!-- Error Message -->
        <div v-if="errorMessage" class="error-banner">
          <div class="error-icon">⚠️</div>
          <div class="error-content">
            <h4>Unable to Load Team Members</h4>
            <p>{{ errorMessage }}</p>
            <button @click="retryLoad" class="retry-btn">Retry</button>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading && !errorMessage" class="loading-banner">
          <div class="spinner"></div>
          <p>Loading team members...</p>
        </div>

        <!-- Your Packages Start -->
        <div v-if="!loading && !errorMessage" class="section section-padding mt-n10 padding-top-50">
          <section class="team-list">
            <!-- <h2>Team Members</h2> -->
            <p>List of uploaded team members and their registration status.</p>
            
            <!-- Show count summary -->
            <div v-if="bulk_users.length > 0" class="summary-card">
              <div class="summary-item">
                <span class="summary-label">Total Members:</span>
                <span class="summary-value">{{ bulk_users.length }}</span>
              </div>
              <div class="summary-item">
                <span class="summary-label">Completed:</span>
                <span class="summary-value success">{{ completedCount }}</span>
              </div>
              <div class="summary-item">
                <span class="summary-label">Pending:</span>
                <span class="summary-value pending">{{ pendingCount }}</span>
              </div>
            </div>

            <Table 
              v-if="bulk_users.length > 0"
              :data="bulk_users" 
              :columnsToShow="['name', 'email', 'mobile', 'registration_status', 'created_date_formatted', 'results']" 
              :headerLabels="{created_date_formatted: 'User Added On'}"
            >
              
              <template #cell-name="{ row }">
                <div class="user-info">
                  <div class="profile-icon">{{ row.first_name.charAt(0) }}{{ row.last_name.charAt(0) }}</div>
                  <p class="user-name">{{ row.first_name }} {{ row.last_name }}</p>
                </div>
              </template>
              <template #cell-registration_status="{ row }">
                <p class="cap badge" :class="getBadgeClass(row.registration_status)">{{row.registration_status}}</p>
              </template>
              <template #cell-results="{ row }">
                 <div class="results-stack">
                    <div
                      class="attempt-card clickable"
                      v-for="(r, i) in row.results.slice(0, 3)"
                      :key="i"
                      @click="showIncorrectRules(r, row)"
                    >
                      <div class="attempt-info">
                        <div class="attempt-label">Attempt {{ i + 1 }}</div>
                        <div class="attempt-date">{{ r.test_attempt_date }}</div>
                      </div>
                      <div class="pentagon">
                        {{ r.correct_ans }}/{{ r.result_length }}
                      </div>
                    </div>
                    <div v-if="row.results.length === 0" class="text-muted">
                      No test attempted
                    </div>
                  </div>
              </template>
            </Table>

            <div v-else-if="!loading" class="empty-state">
              <p>No team members found. Upload users to get started.</p>
              <router-link to="/dashboard/organization/user-upload" class="btn btn-primary">
                Add Team Members
              </router-link>
            </div>
          </section> 
        </div>
        <!-- Packages End -->

      </div>
    </div>
    <!-- Page Content Wrapper End -->

    <!-- Modal for Incorrect Rules -->
    <div v-if="showModal" class="modal-overlay" @click="closeModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>Incorrect Answers - Rules</h3>
          <button class="close-btn" @click="closeModal">&times;</button>
        </div>
        <div class="modal-body">
          <div v-if="loadingRules" class="loading-state">
            <div class="spinner"></div>
            <p>Loading incorrect rules...</p>
          </div>
          <div v-else-if="incorrectRules.length > 0">
            <p class="modal-info">
              <strong>{{ selectedUser }}</strong><br>
              Test Date: <strong>{{ selectedTestDate }}</strong><br>
              Score: <strong>{{ selectedScore }}</strong>
            </p>
            <div class="rules-section">
              <h4>Rules to Review:</h4>
              <div class="rules-list">
                <div
                  v-for="rule in incorrectRules"
                  :key="rule.ruleId"
                  class="rule-item"
                >
                  <span class="rule-id">{{ formatRuleText(rule.ruleId) }}</span>
                  <span class="rule-desc" v-if="rule.description">{{ formatRuleText(rule.description) }}</span>
                </div>
              </div>
            </div>
          </div>
          <div v-else-if="rulesError" class="error-state">
            <p>{{ rulesError }}</p>
          </div>
          <div v-else class="no-data-state">
            <p>No incorrect rules found.</p>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { jwtDecode } from "jwt-decode";
import { getUserDetails } from '@/services/userService';
import Table from '@/components/reusable/Table.vue';
import { getBulkUserDetails, getIncorrectRules } from '@/services/bulkUserService';

export default {
  name: 'OrgUsers',
  components:{Table},
  data() {
    return {
      user: {},
      bulk_users: [],
      selectedFile: null,
      message: '',
      showModal: false,
      loadingRules: false,
      incorrectRules: [],
      rulesError: null,
      selectedUser: '',
      selectedTestDate: '',
      selectedScore: '',
      loading: true,
      errorMessage: null
    };
  },

  computed: {
    completedCount() {
      return this.bulk_users.filter(u => u.registration_status === 'completed').length;
    },
    pendingCount() {
      return this.bulk_users.filter(u => u.registration_status === 'pending').length;
    }
  },

  async mounted(){
    console.log('=== OrgUsers Component Mounted ===');
    
    try {
      const decoded = jwtDecode(localStorage.getItem('access_token'));
      const token_info = decoded;
      
      console.log('Token info:', {
        id: token_info.id,
        email: token_info.email,
        role: token_info.role
      });

      // Check if user has manager role
      if (token_info.role !== 'manager') {
        this.errorMessage = 'Access denied. Only organization managers can view this page.';
        this.loading = false;
        return;
      }

      this.user = await getUserDetails(token_info.email);
      console.log('User details loaded:', this.user.email);
      
      await this.bulkUsers();
    } catch (error) {
      console.error('Error in mounted:', error);
      this.errorMessage = 'Failed to load page. Please try refreshing.';
      this.loading = false;
    }
  },

  methods: {
    async bulkUsers() {
      console.log('=== Fetching bulk users ===');
      console.log('Manager email:', this.user.email);
      
      this.loading = true;
      this.errorMessage = null;

      try {
        let res = await getBulkUserDetails(this.user.email);
        this.bulk_users = res.data;
        
        console.log('SUCCESS: Loaded', res.data.length, 'team members');
        console.log('Data:', res.data);
        
        this.loading = false;
      } catch (error) {
        console.error('ERROR loading bulk users:', error);
        
        // Detailed error logging
        if (error.response) {
          console.error('Response status:', error.response.status);
          console.error('Response data:', error.response.data);
          console.error('Request URL:', error.config?.url);
          
          // Set appropriate error message based on status
          if (error.response.status === 404) {
            this.errorMessage = 'Team members endpoint not found. Please ensure the backend routes are properly configured. Check that /api/bulkUsers routes are registered in your backend.';
          } else if (error.response.status === 403) {
            this.errorMessage = 'Access denied. Your account may not have the required permissions. Please contact support.';
          } else if (error.response.status === 500) {
            this.errorMessage = 'Server error occurred. Please try again later or contact support.';
          } else {
            this.errorMessage = `Failed to load team members: ${error.response.data?.message || error.message}`;
          }
        } else if (error.request) {
          console.error('No response received');
          this.errorMessage = 'Cannot connect to server. Please check if the backend is running.';
        } else {
          console.error('Request setup error:', error.message);
          this.errorMessage = `Request failed: ${error.message}`;
        }
        
        this.loading = false;
      }
    },

    async retryLoad() {
      await this.bulkUsers();
    },

    getBadgeClass(txt) {
      console.log(txt);
      switch (txt) {
        case 'pending':
          return 'badge-danger';
        case 'completed':
          return 'badge-success';
      }
    },

    async showIncorrectRules(result, user) {
      this.showModal = true;
      this.loadingRules = true;
      this.incorrectRules = [];
      this.rulesError = null;
      this.selectedUser = `${user.first_name} ${user.last_name}`;
      this.selectedTestDate = result.test_attempt_date;
      this.selectedScore = `${result.correct_ans}/${result.result_length}`;

      try {
        const response = await getIncorrectRules(result.test_result_id.$oid || result.test_result_id);
        this.incorrectRules = response.data.incorrectRules; // now array of {ruleId, description}
      } catch (error) {
        console.error('Error fetching incorrect rules:', error);
        this.rulesError = 'Failed to load incorrect rules. Please try again.';
      } finally {
        this.loadingRules = false;
      }
    },

    formatRuleText(text) {
      if (!text) return '';
      // Display only, does not affect stored data: strip trailing sub-clause references like (a), (b), (c)
      return text.replace(/\s*\([a-z]\)\.?/gi, '').trim();
    },

    closeModal() {
      this.showModal = false;
      this.incorrectRules = [];
      this.rulesError = null;
      this.selectedUser = '';
      this.selectedTestDate = '';
      this.selectedScore = '';
    }
  }
}
</script>

<style scoped>
/* Error Banner */
.error-banner {
  background: #fff3cd;
  border: 2px solid #ffc107;
  border-radius: 8px;
  padding: 20px;
  margin: 20px 0;
  display: flex;
  align-items: center;
  gap: 15px;
}

.error-icon {
  font-size: 32px;
}

.error-content h4 {
  margin: 0 0 8px 0;
  color: #856404;
}

.error-content p {
  margin: 0 0 12px 0;
  color: #856404;
}

.retry-btn {
  background: #ffc107;
  color: #000;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 600;
}

.retry-btn:hover {
  background: #ffca2c;
}

/* Loading Banner */
.loading-banner {
  text-align: center;
  padding: 40px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #309255;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Summary Card */
.summary-card {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  display: flex;
  gap: 30px;
  flex-wrap: wrap;
}

.summary-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.summary-label {
  font-size: 14px;
  color: #666;
}

.summary-value {
  font-size: 24px;
  font-weight: 700;
  color: #333;
}

.summary-value.success {
  color: #28a745;
}

.summary-value.pending {
  color: #ffc107;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #666;
}

.empty-state p {
  margin-bottom: 20px;
  font-size: 16px;
}

/* Rest of existing styles... */
.team-list {
  padding: 20px 0;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.profile-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #309255;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
}

.user-name {
  margin: 0;
  font-weight: 500;
}

.badge {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  display: inline-block;
}

.badge-success {
  background: #d4edda;
  color: #155724;
}

.badge-danger {
  background: #f8d7da;
  color: #721c24;
}

.cap {
  text-transform: capitalize;
}

.results-stack {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.attempt-card {
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 8px 12px;
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 140px;
  transition: all 0.2s;
}

.attempt-card.clickable {
  cursor: pointer;
}

.attempt-card.clickable:hover {
  border-color: #309255;
  box-shadow: 0 2px 8px rgba(48, 146, 85, 0.2);
}

.attempt-info {
  flex: 1;
}

.attempt-label {
  font-size: 11px;
  color: #666;
  font-weight: 600;
  text-transform: uppercase;
}

.attempt-date {
  font-size: 12px;
  color: #999;
}

.pentagon {
  background: #309255;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: 700;
  font-size: 13px;
}

.text-muted {
  color: #999;
  font-size: 13px;
  font-style: italic;
}

/* Modal styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 12px;
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
}

.modal-header {
  padding: 20px;
  border-bottom: 1px solid #dee2e6;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  margin: 0;
}

.close-btn {
  background: none;
  border: none;
  font-size: 28px;
  cursor: pointer;
  color: #999;
  line-height: 1;
  padding: 0;
}

.close-btn:hover {
  color: #333;
}

.modal-body {
  padding: 20px;
}

.loading-state {
  text-align: center;
  padding: 40px 20px;
}

.modal-info {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 20px;
  line-height: 1.8;
}

.rules-section h4 {
  margin: 0 0 12px 0;
  font-size: 16px;
}

.rules-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.rule-badge {
  background: #309255;
  color: white;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
}

.error-state,
.no-data-state {
  text-align: center;
  padding: 40px 20px;
  color: #666;
}

.rule-item {
  display: flex;
  align-items: center;
  gap: 15px;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 10px 15px;
}

.rule-id {
  background: #309255;
  color: white;
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
  min-width: 90px;
  text-align: center;
}

.rule-desc {
  font-size: 14px;
  color: #52565b;
  font-weight: 400;
}
</style>